import type { RumInitConfiguration, DatadogRum } from '@datadog/browser-rum';

import { getTrackingConsent, updateConsentValues } from './consent';
import { onConsentChange } from '../utils/cookie-consent-helpers';

// Re-export for backwards compatibility with existing imports
export { getTrackingConsent };

/**
 * Shared Datadog RUM site - same across all BetterUp frontends.
 */
export const DATADOG_SITE = 'datadoghq.com';

/**
 * Default excluded activity URLs for Datadog RUM.
 * These URLs are excluded from activity tracking across all frontends.
 */
export const DEFAULT_EXCLUDED_ACTIVITY_URLS: Array<string | RegExp> = [
  /^https:\/\/analytics\.betterup\.co/, // analytics.betterup.co
  /^https:\/.*\.zdassets\.com/, // *.zdassets.com (Zendesk)
  /^https:\/.*\.forethought\.ai/, // *.forethought.ai
  /^https:\/\/segment-api\.betterup\.co/, // segment-api.betterup.co
  /^https:\/.*\.braze\.com\/api/, // *.braze.com/api
  /^https:\/\/api\.sprig\.com/, // api.sprig.com
  /^https:\/.*\.amplitude\.com/, // *.amplitude.com
  /excludeFromDatadogActivity=true/, // standard unencoded format
  /excludeFromDatadogActivity%3Dtrue/, // URL encoded format without =
  /excludeFromDatadogActivity(%3D|=)true(&|$)/, // both variants with proper termination
];

/**
 * Default allowed tracing URLs for distributed tracing.
 * Enables APM trace correlation for BetterUp domains.
 */
export const DEFAULT_ALLOWED_TRACING_URLS: Array<string | RegExp> = [
  /^https:\/\/.*\.betterup\.[co|io|eu]/,
];

/**
 * Required configuration that callers must provide.
 */
export interface DatadogCallerConfig {
  /** Datadog application ID */
  applicationId: string;
  /** Datadog client token */
  clientToken: string;
  /** Service name (e.g., 'buapp-web-us', 'buapp-development-us') */
  service: string;
  /** Environment (e.g., 'production', 'staging', 'development') */
  env: string;
  /** Application version (typically git SHA) */
  version: string;
  /** Session sample rate (0-100). Defaults to 100. */
  sessionSampleRate?: number;
}

/**
 * Optional configuration overrides for caller-specific behavior.
 */
export interface DatadogInitOptions {
  /** Additional URLs to exclude from activity tracking (merged with defaults) */
  additionalExcludedActivityUrls?: Array<string | RegExp>;
  /** Whether views are tracked manually. Defaults to true (for SPAs). */
  trackViewsManually?: boolean;
  /**
   * Custom beforeSend handler for filtering/modifying events.
   * Return false to discard the event, true or undefined to keep it.
   */
  beforeSend?: RumInitConfiguration['beforeSend'];
  /** Whether to include allowedTracingUrls for distributed tracing. Defaults to true. */
  enableTracing?: boolean;
  /** Override any RumInitConfiguration property */
  overrides?: Partial<RumInitConfiguration>;
}

/**
 * Check if Datadog configuration is present and valid.
 */
export function isDatadogEnabled(
  config: Partial<DatadogCallerConfig>
): boolean {
  return !!(config.applicationId?.length && config.clientToken?.length);
}

/**
 * Build the full Datadog RUM configuration from caller config and options.
 *
 * This is a pure function that returns the configuration object.
 * Callers are responsible for calling `datadogRum.init()` or `DD_RUM.init()`.
 *
 * @param config - Required configuration (applicationId, clientToken, etc.)
 * @param options - Optional overrides for caller-specific behavior
 * @returns Complete RumInitConfiguration ready to pass to init()
 */
export function buildDatadogConfig(
  config: DatadogCallerConfig,
  options: DatadogInitOptions = {}
): RumInitConfiguration {
  const {
    additionalExcludedActivityUrls = [],
    trackViewsManually = true,
    beforeSend,
    enableTracing = true,
    overrides = {},
  } = options;

  const baseConfig: RumInitConfiguration = {
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: DATADOG_SITE,
    service: config.service,
    env: config.env,
    version: config.version,
    sessionSampleRate: config.sessionSampleRate ?? 100,
    trackingConsent: getTrackingConsent(),
    // Session replay settings - manual control for all frontends
    sessionReplaySampleRate: 100,
    startSessionReplayRecordingManually: true,
    // Tracking settings - consistent across all frontends
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
    trackViewsManually,
    // Excluded URLs - merge defaults with caller additions
    excludedActivityUrls: [
      ...DEFAULT_EXCLUDED_ACTIVITY_URLS,
      ...additionalExcludedActivityUrls,
    ],
  };

  // Add distributed tracing URLs if enabled
  if (enableTracing) {
    baseConfig.allowedTracingUrls = DEFAULT_ALLOWED_TRACING_URLS;
  }

  // Add beforeSend if provided
  if (beforeSend) {
    baseConfig.beforeSend = beforeSend;
  }

  // Apply any additional overrides
  return {
    ...baseConfig,
    ...overrides,
  };
}

/**
 * Minimal interface for DD_RUM that's compatible with both:
 * - The @datadog/browser-rum npm package (DatadogRum)
 * - The window.DD_RUM stub used in Rails
 *
 * This allows us to use the same initialization logic for both.
 */
export interface DatadogRumInstance {
  init: (config: RumInitConfiguration) => void;
  setTrackingConsent: (consent: 'granted' | 'not-granted') => void;
  getInitConfiguration?: () => RumInitConfiguration | undefined;
}

/**
 * Result of initDatadog, containing info about the initialization.
 */
export interface InitDatadogResult {
  /** Whether initialization was successful */
  initialized: boolean;
  /** Whether an existing instance was found (skipped initialization) */
  existingInstance: boolean;
}

/**
 * Initialize Datadog RUM with the provided configuration and set up consent tracking.
 *
 * This function:
 * 1. Builds the RUM configuration using buildDatadogConfig
 * 2. Initializes the RUM instance
 * 3. Sets up a listener to update tracking consent when cookie consent changes
 *
 * @param rum - The Datadog RUM instance (datadogRum from npm or window.DD_RUM)
 * @param config - Required configuration (applicationId, clientToken, etc.)
 * @param options - Optional overrides for caller-specific behavior
 * @returns Result indicating whether initialization was successful
 */
export function initDatadog(
  rum: DatadogRumInstance,
  config: DatadogCallerConfig,
  options: DatadogInitOptions = {}
): InitDatadogResult {
  const consentCallback = () => updateConsentValues(rum as DatadogRum);

  // Check if already initialized
  if (rum.getInitConfiguration?.()) {
    // Even if already initialized, sync consent in case it changed
    consentCallback();
    return { initialized: true, existingInstance: true };
  }

  // Build and apply configuration
  const rumConfig = buildDatadogConfig(config, options);
  rum.init(rumConfig);

  // Set up consent change listener to update Datadog when consent changes
  onConsentChange(consentCallback);

  // Also call immediately in case consent changed before listener was registered
  consentCallback();

  return { initialized: true, existingInstance: false };
}
